<template lang="pug">
    include ../../media/pug/mixins
    +b.area(
        v-bind:style="areaStyles"
        v-bind:class="areaClassList"
    )
        +e.view
            img(
                v-bind:src="image"
                v-bind:style="imgStyles"
            )
        +e.box(v-bind:style="areaBoxStyles")
            +e.line(
                v-for="line in lines"
                v-bind:key="'line' + line"
                ref="line"
                v-bind:class="`area__line_${line}`"
                v-on:mousedown="onMouseDown(line, $event)"
                v-on:touchstart="onMouseDown(line, $event)"
            )
            +e.pointer(
                v-for="pointer in pointers"
                v-bind:key="'pointer' + pointer"
                v-bind:class="`area__pointer_${pointer}`"
                v-on:mousedown="onMouseDown(pointer, $event)"
                v-on:touchstart="onMouseDown(pointer, $event)"
            )
        +e.content
            document-view-img-mark.area__mark(
                v-if="type === 'area'"
                v-bind:type="type"
                v-bind:is-active="isActive"
                v-bind:text="text"
                v-bind:popover="popoverOffset"
                v-bind="markPosition"
                v-bind:box-width="boxWidth"
                v-bind:box-height="boxHeight"
                v-on:edit="$emit('edit')"
                v-on:change-text="$emit('change-text', $event)"
                v-on:confirm="$emit('confirm', $event)"
            )

</template>


<script>
import { mapGetters } from 'vuex';
import DocumentViewImgMark from '@/components/document-views/DocumentViewImgMark';

export default {
    components: {
        DocumentViewImgMark
    },
    props: {
        type: {
            type: String,
            default: 'area'
        },
        startX: {
            type: Number,
            required: true,
            default: 0
        },
        startY: {
            type: Number,
            required: true,
            default: 0
        },
        endX: {
            type: Number,
            required: true,
            default: 0
        },
        endY: {
            type: Number,
            required: true,
            default: 0
        },
        text: {
            type: String,
            default: ''
        },
        isActive: {
            type: Boolean,
            default: false,
            required: true
        },
        boxWidth: {
            type: Number,
            default: 0
        },
        boxHeight: {
            type: Number,
            default: 0
        },
        image: {
            type: String,
            default: '',
            required: true
        }
    },
    data() {
        return {
            tooltipHeight: 57,
            lines: ['n', 'e', 's', 'w'],
            pointers: ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'],
            startWidth: 0,
            startHeight: 0,
            currentResize: {
                startX: 0,
                startY: 0,
                endX: 0,
                endY: 0
            },
            prevResize: {
                startX: 0,
                startY: 0,
                endX: 0,
                endY: 0
            },
            currentDir: '',
            startPoint: {
                x: 0,
                y: 0
            },
            endPoint: {
                x: 0,
                y: 0
            }
        };
    },
    computed: {
        ...mapGetters([
            'isMobile',
            'getZoom',
            'getMainImgZoomHeight',
            'getMainImgZoomWidth',
            'getActiveArea',
            'isMainImgLandscape',
            'getAreas'
        ]),
        scaledPosition() {
            return {
                startX: (this.startX + this.currentResize.startX) * this.getZoom,
                startY: (this.startY + this.currentResize.startY) * this.getZoom,
                endX: (this.endX + this.currentResize.endX) * this.getZoom,
                endY: (this.endY + this.currentResize.endY) * this.getZoom
            };
        },
        boxOrigin() {
            const rotate = +this.$route.query.rotate;
            if (rotate === -90 || rotate === 270) {
                return {
                    x: 50 + (this.width / this.height - 1) * 100 / 2,
                    y: 50
                };
            } if (rotate === 90 || rotate === -270) {
                return {
                    x: 50,
                    y: 50 + (this.height / this.width - 1) * 100 / 2
                };
            }
            return {
                x: 50,
                y: 50
            };
        },
        width() {
            return Math.abs(this.currentPosition.endX - this.currentPosition.startX);
        },
        height() {
            return Math.abs(this.currentPosition.endY - this.currentPosition.startY);
        },
        areaBoxStyles() {
            return {
                transform: `rotate(${-this.$route.query.rotate}deg)`,
                width: this.isMainImgLandscape ? `${this.height}px` : `${this.width}px`,
                height: this.isMainImgLandscape ? `${this.width}px` : `${this.height}px`,
                transformOrigin: `${this.boxOrigin.x}% ${this.boxOrigin.y}%`
            };
        },
        currentPosition() {
            const {
                startX, startY, endX, endY
            } = this.scaledPosition;
            let x = Math.min(startX, endX);
            let y = Math.min(startY, endY);
            let x1 = Math.max(startX, endX);
            let y1 = Math.max(startY, endY);

            // bounds
            if (x1 >= this.getMainImgZoomWidth) {
                x1 = this.getMainImgZoomWidth;
            }

            if (y1 >= this.getMainImgZoomHeight) {
                y1 = this.getMainImgZoomHeight;
            }

            if (x <= 0) {
                x = 0;
            }

            if (y <= 0) {
                y = 0;
            }
            return {
                startX: x,
                startY: y,
                endX: x1,
                endY: y1
            };
        },
        areaStyles() {
            return {
                width: `${this.width}px`,
                height: `${this.height}px`,
                transform: `
                    translate(${this.currentPosition.startX}px, ${this.currentPosition.startY}px)
                `
            };
        },
        markPosition() {
            switch (+this.$route.query.rotate) {
            case 90:
            case -270:
                return {
                    x: -(this.boxWidth + 10),
                    y: this.height / 2 - this.boxHeight / 2
                };
            case 180:
            case -180:
                return {
                    x: this.width / 2 - this.boxWidth / 2,
                    y: this.height + 10
                };
            case 270:
            case -90:
                return {
                    x: this.width + 10,
                    y: this.height / 2 - this.boxHeight / 2
                };
            default:
                return {
                    x: this.width / 2 - this.boxWidth / 2,
                    y: -(this.boxHeight + 10)
                };
            }
        },
        areaClassList() {
            return [
                { area_is_active: this.isActive }
            ];
        },
        popoverOffset() {
            if (this.isAreaOnBoundaryY) {
                return { y: this.boxHeight + 10 };
            }
            if (this.isMainImgLandscape) {
                return { y: this.width + this.boxHeight + 25 };
            }
            return { y: this.height + this.boxHeight + 25 };
        },
        isAreaOnBoundaryY() {
            switch (+this.$route.query.rotate) {
            case 90:
            case -270:
                return this.currentPosition.endX + this.tooltipHeight >= this.getMainImgZoomWidth;
            case -180:
            case 180:
                return this.currentPosition.startY - this.tooltipHeight <= 0;
            case -90:
            case 270:
                return this.currentPosition.startX - this.tooltipHeight <= 0;
            default:
                return this.currentPosition.endY + this.tooltipHeight >= this.getMainImgZoomHeight;
            }
        },
        imgStyles() {
            return {
                width: `${this.getMainImgZoomWidth}px`,
                height: `${this.getMainImgZoomHeight}px`,
                transform: `
                    translate(-${(this.currentPosition.startX)}px, -${(this.currentPosition.startY)}px)
                    `
            };
        }
    },
    mounted() {
        document.querySelector('.document-view-img__container img').ondragstart = () => false;
    },
    methods: {
        setPoint(e, which) {
            if (e.touches) {
                this[which].x = e.touches[0].clientX / this.getZoom;
                this[which].y = e.touches[0].clientY / this.getZoom;
            } else {
                this[which].x = e.pageX / this.getZoom;
                this[which].y = e.pageY / this.getZoom;
            }
        },
        onMouseDown(dir, e) {
            e.stopPropagation();
            this.setPoint(e, 'startPoint');
            this.currentDir = dir;
            this.startWidth = this.width / this.getZoom;
            this.startHeight = this.height / this.getZoom;
            this.initResize();
        },
        initResize() {
            ['mousemove', 'touchmove'].forEach((event) => {
                document.addEventListener(event, this.onMouseMove);
            });
            ['mouseup', 'touchend'].forEach((event) => {
                document.addEventListener(event, this.onMouseUp);
            });
        },
        onMouseMove(e) {
            e.stopPropagation();
            this.setPoint(e, 'endPoint');
            const { diffX, diffY } = this.setDiffrence();
            /*
            * для зеркальных поворотов например 0 и 180. значения для ресайза при повороте на 180
            * берутся из зеркальных сторон 0 (default) - дефолтного случая.
            * т.е nw(nortwest) поворота 0 равен se(southeast) поворота 180
            */
            switch (+this.$route.query.rotate) {
            case 90:
            case -270:
                switch (this.currentDir) {
                case 'nw':
                    this.setStartResize(diffY, 'startX');
                    this.setEndResize(diffX, 'endY');
                    break;
                case 'n':
                    this.setStartResize(diffY, 'startX');
                    break;
                case 'ne':
                    this.setStartResize(diffY, 'startX');
                    this.setStartResize(diffX, 'startY');
                    break;
                case 'e':
                    this.setStartResize(diffX, 'startY');
                    break;
                case 'se':
                    this.setEndResize(diffY, 'endX');
                    this.setStartResize(diffX, 'startY');
                    break;
                case 's':
                    this.setEndResize(diffY, 'endX');
                    break;
                case 'sw':
                    this.setEndResize(diffY, 'endX');
                    this.setEndResize(diffX, 'endY');
                    break;
                case 'w':
                    this.setEndResize(diffX, 'endY');
                    break;
                default:
                    return false;
                }
                break;
            case 180:
            case -180:
                switch (this.currentDir) {
                case 'nw':
                    this.setEndResize(diffX, 'endX');
                    this.setEndResize(diffY, 'endY');
                    break;
                case 'n':
                    this.setEndResize(diffY, 'endY');
                    break;
                case 'ne':
                    this.setStartResize(diffX, 'startX');
                    this.setEndResize(diffY, 'endY');
                    break;
                case 'e':
                    this.setStartResize(diffX, 'startX');
                    break;
                case 'se':
                    this.setStartResize(diffX, 'startX');
                    this.setStartResize(diffY, 'startY');
                    break;
                case 's':
                    this.setStartResize(diffY, 'startY');
                    break;
                case 'sw':
                    this.setEndResize(diffX, 'endX');
                    this.setStartResize(diffY, 'startY');
                    break;
                case 'w':
                    this.setEndResize(diffX, 'endX');
                    break;
                default:
                    return false;
                }
                break;
            case -90:
            case 270:
                switch (this.currentDir) {
                case 'nw':
                    this.setEndResize(diffY, 'endX');
                    this.setStartResize(diffX, 'startY');
                    break;
                case 'n':
                    this.setEndResize(diffY, 'endX');
                    break;
                case 'ne':
                    this.setEndResize(diffY, 'endX');
                    this.setEndResize(diffX, 'endY');
                    break;
                case 'e':
                    this.setEndResize(diffX, 'endY');
                    break;
                case 'se':
                    this.setStartResize(diffY, 'startX');
                    this.setEndResize(diffX, 'endY');
                    break;
                case 's':
                    this.setStartResize(diffY, 'startX');
                    break;
                case 'sw':
                    this.setStartResize(diffY, 'startX');
                    this.setStartResize(diffX, 'startY');
                    break;
                case 'w':
                    this.setStartResize(diffX, 'startY');
                    break;
                default:
                    return false;
                }
                break;
            default:
                switch (this.currentDir) {
                case 'nw':
                    this.setStartResize(diffX, 'startX');
                    this.setStartResize(diffY, 'startY');
                    break;
                case 'n':
                    this.setStartResize(diffY, 'startY');
                    break;
                case 'ne':
                    this.setEndResize(diffX, 'endX');
                    this.setStartResize(diffY, 'startY');
                    break;
                case 'e':
                    this.setEndResize(diffX, 'endX');
                    break;
                case 'se':
                    this.setEndResize(diffX, 'endX');
                    this.setEndResize(diffY, 'endY');
                    break;
                case 's':
                    this.setEndResize(diffY, 'endY');
                    break;
                case 'sw':
                    this.setStartResize(diffX, 'startX');
                    this.setEndResize(diffY, 'endY');
                    break;
                case 'w':
                    this.setStartResize(diffX, 'startX');
                    break;
                default:
                    return false;
                }
            }
            return false;
        },
        setDiffrence() {
            switch (+this.$route.query.rotate) {
            case 90:
            case -270:
                return {
                    diffX: this.startPoint.x - this.endPoint.x,
                    diffY: this.endPoint.y - this.startPoint.y
                };
            case 180:
            case -180:
                return {
                    diffX: this.startPoint.x - this.endPoint.x,
                    diffY: this.startPoint.y - this.endPoint.y
                };
            case -90:
            case 270:
                return {
                    diffX: this.endPoint.x - this.startPoint.x,
                    diffY: this.startPoint.y - this.endPoint.y
                };
            default:
                return {
                    diffX: this.endPoint.x - this.startPoint.x,
                    diffY: this.endPoint.y - this.startPoint.y
                };
            }
        },
        onMouseUp() {
            this.prevResize.startX = this.currentResize.startX;
            this.prevResize.endX = this.currentResize.endX;
            this.prevResize.startY = this.currentResize.startY;
            this.prevResize.endY = this.currentResize.endY;
            ['mousemove', 'touchmove'].forEach((event) => {
                document.removeEventListener(event, this.onMouseMove);
            });
            ['mouseup', 'touchend'].forEach((event) => {
                document.removeEventListener(event, this.onMouseUp);
            });
        },
        setEndResize(diff, start) {
            /*
            * если разница в перетягивании(diff) перешла за противоположную границу(size),
            * то граница с которой тянем становится фиксированной, а меняется противоположый ресайз.
            */
            const size = start === 'endX' ? 'startWidth' : 'startHeight';
            const end = start === 'endX' ? 'startX' : 'startY';
            if (-diff > this[size]) {
                const newStart = this.prevResize[start] - this[size];
                if (this.currentResize[start] !== newStart) {
                    this.currentResize[start] = newStart;
                }
                this.currentResize[end] = (diff + this.prevResize[end]) + this[size];
            } else {
                this.currentResize[start] = diff + this.prevResize[start];
            }
        },
        setStartResize(diff, start) {
            /*
            * если разница в перетягивании(diff) перешла за противоположную границу(size),
            * то граница с которой тянем становится фиксированнойm, а меняется противоположый ресайз.
            */
            const size = start === 'startX' ? 'startWidth' : 'startHeight';
            const end = start === 'startX' ? 'endX' : 'endY';
            if (diff > this[size]) {
                const newStart = this[size] + this.prevResize[start];
                if (this.currentResize[start] !== newStart) {
                    this.currentResize[start] = newStart;
                }
                this.currentResize[end] = (diff + this.prevResize[end]) - this[size];
            } else {
                this.currentResize[start] = diff + this.prevResize[start];
            }
        }
    }
};
</script>

<style lang="sass" src="@/media/sass/components/document-views/document-view-img-area.sass"></style>
